import { expandCollapsedAncestors } from '@/utils/deeplink/expandCollapsedAncestors'
import {
	findHashTargetElement,
	getHashTargetId,
	isElementRendered,
} from '@/utils/deeplink/hashTarget'
import type { Router } from 'next/router'
import { useEffect, useRef } from 'react'

/**
 * How long to wait for the anchor to appear in the DOM. Rich-text linked items such as
 * `ui_accordion` are resolved client-side, so on those pages the anchor is absent from the
 * server HTML and only exists a moment after hydration.
 */
const TARGET_TIMEOUT_MS = 10000
/** Give up waiting for an anchor that never becomes visible */
const RENDER_TIMEOUT_MS = 3000
/** How often to re-check while waiting */
const POLL_MS = 50
/** MUI's `Collapse` runs for 300ms; scroll once the new height has settled */
const EXPAND_TRANSITION_MS = 400
/** Preserves the settle delay the previous `router.scrollToHash` call used */
const SETTLE_MS = 300

/** How long to keep correcting the scroll while the page is still settling */
const STABILISE_TIMEOUT_MS = 4000
const STABILISE_POLL_MS = 100
/** Consecutive unchanged readings before we accept the anchor has stopped moving */
const STABLE_READINGS_REQUIRED = 3

/** A genuine interaction means the reader has taken over; stop trying to scroll them */
const USER_INTERACTION_EVENTS = ['wheel', 'touchmove', 'keydown'] as const

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Polls until `produce` returns something truthy, or the timeout expires.
 *
 * Uses `setTimeout` rather than `requestAnimationFrame` on purpose: rAF callbacks are paused
 * while a tab is not visible, so a link opened in a background tab would wait forever.
 */
const pollUntil = <T>(
	produce: () => T | null,
	timeoutMs: number,
	isActive: () => boolean,
): Promise<T | null> =>
	new Promise((resolve) => {
		const startedAt = Date.now()

		const poll = () => {
			if (!isActive()) return resolve(null)

			const value = produce()
			if (value) return resolve(value)

			if (Date.now() - startedAt > timeoutMs) return resolve(null)
			setTimeout(poll, POLL_MS)
		}

		poll()
	})

/**
 * Scrolls to the anchor, then keeps correcting while it is still moving.
 *
 * A single `scrollIntoView()` at a fixed delay is a race: anything that changes layout after it —
 * an accordion still animating open, late images, fonts, MathJax typesetting — silently leaves the
 * reader parked at a stale offset with no error. Comparing the anchor's absolute document position
 * between polls self-adapts instead of guessing a delay: `scrollIntoView` moves the viewport, not
 * the anchor, so once the document stops reflowing the reading repeats and we stop.
 */
const scrollUntilSettled = async (isActive: () => boolean) => {
	const deadline = Date.now() + STABILISE_TIMEOUT_MS
	let lastDocTop: number | null = null
	let stableReadings = 0

	while (
		isActive() &&
		Date.now() < deadline &&
		stableReadings < STABLE_READINGS_REQUIRED
	) {
		const el = findHashTargetElement()
		if (!el) return

		const docTop = Math.round(
			el.getBoundingClientRect().top + window.scrollY,
		)

		if (docTop === lastDocTop) {
			stableReadings += 1
		} else {
			// Vertical offset for sticky headers comes from the `scroll-margin-top` already
			// declared on headings, so the default `block: 'start'` is what we want.
			el.scrollIntoView()
			lastDocTop = docTop
			stableReadings = 0
		}

		await delay(STABILISE_POLL_MS)
	}
}

/**
 * Makes deep links land on their target even when it sits inside a collapsed accordion:
 * waits for the anchor, expands whichever accordions it is nested in, then scrolls to it.
 *
 * Mount once, at app level.
 */
export const useDeepLinkToHashTarget = (router: Router) => {
	const inFlightHashRef = useRef<string>('')
	const runTokenRef = useRef(0)

	useEffect(() => {
		let isMounted = true
		let isInterrupted = false

		const onUserInteraction = () => {
			isInterrupted = true
		}

		const goToHashTarget = async () => {
			const hashId = getHashTargetId()
			if (!hashId) return

			// A route change to another page's anchor fires both `hashchange` and
			// `routeChangeComplete`. Without this guard the second run would click an accordion
			// the first run just opened — before React re-rendered it — and close it again.
			if (inFlightHashRef.current === hashId) return

			inFlightHashRef.current = hashId
			isInterrupted = false
			const runToken = ++runTokenRef.current
			// A newer hash supersedes this run; so does the reader scrolling for themselves.
			const isRunActive = () =>
				isMounted && !isInterrupted && runTokenRef.current === runToken

			try {
				// The anchor may not exist yet — see TARGET_TIMEOUT_MS.
				const target = await pollUntil(
					findHashTargetElement,
					TARGET_TIMEOUT_MS,
					isRunActive,
				)
				if (!target) return

				const didExpand = expandCollapsedAncestors(target)

				// Re-resolve by id rather than reusing `target`: React can replace the node
				// while the accordion opens.
				const rendered = await pollUntil(
					() => {
						const el = findHashTargetElement()
						return el && isElementRendered(el) ? el : null
					},
					RENDER_TIMEOUT_MS,
					isRunActive,
				)
				if (!rendered) return

				await delay(didExpand ? EXPAND_TRANSITION_MS : SETTLE_MS)
				if (!isRunActive()) return

				await scrollUntilSettled(isRunActive)
			} finally {
				if (runTokenRef.current === runToken) {
					inFlightHashRef.current = ''
				}
			}
		}

		goToHashTarget()
		window.addEventListener('hashchange', goToHashTarget)
		router.events.on('routeChangeComplete', goToHashTarget)
		USER_INTERACTION_EVENTS.forEach((event) =>
			window.addEventListener(event, onUserInteraction, {
				passive: true,
			}),
		)

		return () => {
			isMounted = false
			window.removeEventListener('hashchange', goToHashTarget)
			router.events.off('routeChangeComplete', goToHashTarget)
			USER_INTERACTION_EVENTS.forEach((event) =>
				window.removeEventListener(event, onUserInteraction),
			)
		}
	}, [router])
}
